import React from 'react'

import { ArticleImage } from 'component-library/publication/ArticleImage/ArticleImage'
import { ArticleVideo } from 'component-library/publication/ArticleVideo/ArticleVideo'
import { VideoOptions, GetVideoQueue } from 'component-library/content/Video/PlayerInterface'
import { VideoDetails } from 'common/client-data-types/VideoDetails'
import { Embed } from 'component-library/publication/Embed/Embed'
import { Blog } from 'component-library/content/Blog/Blog'
import { Paragraph } from 'component-library/publication/Paragraph/Paragraph'
import { Pullquote } from 'component-library/publication/Pullquote/Pullquote'
import { List } from 'component-library/typography/List/List'
import { Subhead } from 'component-library/typography/Subhead/Subhead'

import {
    StyledBlockContent,
    TwinImageWrapper,
} from 'component-library/publication/ArticleBlockContent/ArticleBlockContent.styled'
import { Logger } from 'typescript-log'
import { InlinePositionedContent } from 'component-library/templates/Publication/Publication.props'
import { VideoQueue } from 'component-library/templates/Publication/lib/get-video-queue'
import { PlayerEvent } from 'common/events/video-event-types'
import { useToggleState, isFeatureEnabled } from 'common/feature-togglings/features'
import { LinkClickedEvent } from 'common/events/component-events'
import { Block } from 'common/client-data-types/blocks'

export interface Props {
    showInlineVideoTitles?: boolean
    blocks: Block[]
    targetedContentElement?: React.ReactElement<any>
    inlinePositionedContent?: InlinePositionedContent[]
    onEvent: (event: PlayerEvent | LinkClickedEvent) => void
    log: Logger
    publicationPathname?: string
    adUnitPath: string
    className?: string
    getVideoQueue?: GetVideoQueue
}

function assertNever(x: Block): never {
    throw new Error('Unexpected object with kind ' + x.kind + '\r\n' + JSON.stringify(x))
}

export const ArticleBlockContent: React.FC<Props> = ({
    blocks,
    targetedContentElement,
    inlinePositionedContent,
    onEvent,
    log,
    publicationPathname,
    showInlineVideoTitles,
    adUnitPath,
    className,
    getVideoQueue,
}) => {
    const toggles = useToggleState()

    if (!blocks) {
        return null
    }

    const contentBlocks = blocks.map<JSX.Element>((block, index) => {
        switch (block.kind) {
            case 'text':
                return (
                    <Paragraph
                        key={index}
                        intentions={block.intentions}
                        text={block.text}
                        onEvent={onEvent}
                    />
                )
            case 'pull-quote':
                return <Pullquote key={index} {...block} />
            case 'embed':
                return (
                    <Embed
                        log={log}
                        key={index}
                        onEvent={onEvent}
                        adUnitPath={adUnitPath}
                        {...block}
                    />
                )
            case 'blog':
                return (
                    <Blog
                        key={index}
                        blogId={block.blogId}
                        provider={block.provider}
                        blogState={block.blogState}
                    />
                )
            case 'twin-image': {
                return (
                    // 'twin-image-wrapper' utility class required to fix a cascade issue outlined in SWM-4447
                    <TwinImageWrapper className="twin-image-wrapper" key={index}>
                        <ArticleImage
                            align={'left'}
                            isTwin
                            key="1"
                            image={block.left}
                            figCaption={{
                                byline: block.left.byline,
                                captionText: block.left.captionText,
                                credit: block.left.credit,
                                kind: 'image',
                            }}
                        />
                        <ArticleImage
                            align={'right'}
                            isTwin
                            key="2"
                            image={block.right}
                            figCaption={{
                                byline: block.right.byline,
                                captionText: block.right.captionText,
                                credit: block.right.credit,
                                kind: 'image',
                            }}
                        />
                    </TwinImageWrapper>
                )
            }
            case 'inline': {
                switch (block.inlineKind) {
                    case 'video': {
                        const heading = block.inlineDetails.captionText || ''
                        const posterImage = block.inlineVideo.posterImage
                        const videoId = block.inlineVideo.id
                        const accountId = block.inlineVideo.accountId
                        const videoType = block.inlineVideo.videoType

                        const videoDetails: VideoDetails = {
                            id: videoId,
                            accountId,
                            heading,
                            posterImage,
                            videoType,
                        }

                        const captionText = block.inlineDetails.captionText

                        const videoOptions: VideoOptions = {
                            adUnitPath,
                            canAutoplayVideos: false,
                            enableSnowplow: isFeatureEnabled(toggles, 'snowplow'),
                            enablePrebid: isFeatureEnabled(toggles, 'prebid-video'),
                            enableNielsenVideoReporting: isFeatureEnabled(
                                toggles,
                                'nielsen-video-events'
                            ),
                        }

                        const showPlaylist = isFeatureEnabled(toggles, 'playlists-on-inline-videos')
                        const isAutoPlayNext = isFeatureEnabled(toggles, 'can-autoplay-next')

                        const autoplayOptions = false

                        const autoplayNextOptions =
                            isAutoPlayNext !== false && showPlaylist && getVideoQueue
                                ? {
                                      delayDuration: 3,
                                  }
                                : false

                        return (
                            <ArticleVideo
                                key={index}
                                captionText={captionText}
                                videoDetails={videoDetails}
                                showTitles={showInlineVideoTitles || false}
                                autoplayOptions={autoplayOptions}
                                autoplayNextOptions={autoplayNextOptions}
                                videoOptions={videoOptions}
                                pauseOverlay={false}
                                isVideoStickyEnabled={false}
                                onEvent={onEvent}
                                getVideoQueue={
                                    showPlaylist && getVideoQueue
                                        ? getVideoQueue
                                        : () =>
                                              Promise.resolve<VideoQueue>({
                                                  items: [],
                                                  type: 'not-playlist-video',
                                              })
                                }
                                showPlaylist={showPlaylist}
                                publicationPathname={publicationPathname}
                            />
                        )
                    }
                    case 'image':
                        return (
                            <ArticleImage
                                align={block.align === 'center' ? undefined : block.align}
                                key={index}
                                image={block.inlineDetails}
                                figCaption={{
                                    byline: block.inlineDetails.byline || undefined,
                                    captionText: block.inlineDetails.captionText,
                                    credit: block.inlineDetails.credit || undefined,
                                    kind: 'image',
                                }}
                            />
                        )
                    default:
                        return assertNever(block)
                }
            }
            case 'ordered-list': {
                return <List key={index} items={block.items} listType="ol" onEvent={onEvent} />
            }
            case 'unordered-list': {
                return <List key={index} items={block.items} listType="ul" onEvent={onEvent} />
            }
            case 'subhead': {
                return (
                    <Subhead
                        key={index}
                        intentions={block.intentions}
                        text={block.text}
                        tag="h2"
                        onEvent={onEvent}
                    />
                )
            }
            case 'competition': {
                return (
                    <Paragraph
                        onEvent={onEvent}
                        intentions={[
                            {
                                kind: 'emphasized',
                                match: 'Competition has now closed.',
                                index: 0,
                            },
                            {
                                kind: 'important',
                                match: 'Competition has now closed.',
                                index: 0,
                            },
                        ]}
                        text="Competition has now closed."
                    />
                )
            }
            default:
                return assertNever(block)
        }
    })

    // Just be aware that if multiple items are passed in with the same position, the last will take precedence
    if (inlinePositionedContent !== undefined && inlinePositionedContent.length > 0) {
        inlinePositionedContent.forEach((inline, index) => {
            const keyedInlineElement = React.cloneElement(inline.element, {
                key: `inlineContentElement-${index}`,
            })

            // if inline.position === undefined it default to 1 as per previous logic, same with -1
            // indexes > conentBlocks just go last
            contentBlocks.splice(
                inline.position !== undefined && inline.position !== -1 ? inline.position : 1,
                0,
                keyedInlineElement
            )
        })
    }

    // Targeted content displays in the middle if content blocks >= 8
    if (targetedContentElement !== undefined) {
        const keyedTargetedElement = React.cloneElement(targetedContentElement, {
            key: 'targetedContentElement',
        })

        if (contentBlocks.length >= 8) {
            const halfwayPoint = Math.floor(contentBlocks.length / 2)
            contentBlocks.splice(halfwayPoint, 0, keyedTargetedElement) // Insert the targetedContentElement
        } else {
            // targetedContentElement displays at the end at content blocks ( < 8 )
            contentBlocks.push(keyedTargetedElement)
        }
    }

    return (
        <StyledBlockContent className={className} id={articleContentID}>
            {contentBlocks}
        </StyledBlockContent>
    )
}
ArticleBlockContent.displayName = 'ArticleBlockContent'
export const articleContentID = 'ArticleContent'
